import { Utils } from '@zaoblako/shared/common/classes/utils';
import { TemplateEntityVariableEnum } from '@zaoblako/shared/common/enums/template.entity.variable.enum';
import { TimeZoneEnum } from '@zaoblako/shared/common/enums/time.zone.enum';
import type { TemplateEngineVariablesValuesInterface } from '@zaoblako/shared/common/interfaces/template.engine.variables.values.interface';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import stringPlaceholder from 'string-placeholder';
import moment from 'moment-timezone';

/**
 * Service class which provides task template variables parsing.
 * Singleton object.
 */
export class TemplateEntityVariableEngine {
  /** Variable placeholder opening brackets */
  private static readonly VARIABLE_OPEN = '{{';

  /** Variable placeholder closing brackets */
  private static readonly VARIABLE_CLOSE = '}}';

  /**
   * Get instance of {@link TemplateEntityVariableEngine}.
   * If it's not created yet, then it will be created.
   */
  public static getInstance(): TemplateEntityVariableEngine {
    if (!TemplateEntityVariableEngine.instance) {
      TemplateEntityVariableEngine.instance = new TemplateEntityVariableEngine();
    }
    return TemplateEntityVariableEngine.instance;
  }

  private static instance: TemplateEntityVariableEngine = null;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   * Parse template string: replace variables placeholders (`{{VARIABLE_NAME}}`)
   * to their values with received data
   *
   * @param str Template string to parse
   * @param [values] Values to replace variables placeholders
   * ({@link TemplateEngineVariablesValuesInterface} type objects)
   * @param [timezone] Time zone to use
   *
   * @returns Rendered string value
   */
  public parse(
    str: string,
    values: TemplateEngineVariablesValuesInterface = {},
    timezone = TimeZoneEnum.EUROPE_MOSCOW,
  ): string {
    const {
      date = new Date(),
      recipientsName,
      contragent,
      company = 'Компания',
      amountOfDebts = 0,
      telegramLink,
    } = values;
    const mDate = moment.tz(date, timezone);

    return stringPlaceholder(
      str,
      {
        [TemplateEntityVariableEnum.CONTRAGENT]: contragent || this.quot(TemplateEntityVariableEnum.CONTRAGENT),
        [TemplateEntityVariableEnum.RECIPIENTS_NAME]: recipientsName || this.quot(TemplateEntityVariableEnum.RECIPIENTS_NAME),
        [TemplateEntityVariableEnum.COMPANY]: company || this.quot(TemplateEntityVariableEnum.COMPANY),
        [TemplateEntityVariableEnum.DATE]: mDate.format('DD.MM.YYYY'),
        [TemplateEntityVariableEnum.AMOUNT_OF_DEBTS]: Utils.formatFloat(amountOfDebts),
        [TemplateEntityVariableEnum.CURRENT_MONTH]: mDate.format('MMMM'),
        [TemplateEntityVariableEnum.PREVIOUS_MONTH]: mDate.clone().subtract(1, 'month').format('MMMM'),
        [TemplateEntityVariableEnum.NEXT_MONTH]: mDate.clone().add(1, 'month').format('MMMM'),
        [TemplateEntityVariableEnum.CURRENT_MONTH_NUMERIC]: mDate.format('MM'),
        [TemplateEntityVariableEnum.PREVIOUS_MONTH_NUMERIC]: mDate.clone().subtract(1, 'month').format('MM'),
        [TemplateEntityVariableEnum.NEXT_MONTH_NUMERIC]: mDate.clone().add(1, 'month').format('MM'),
        [TemplateEntityVariableEnum.CURRENT_QUARTER]: mDate.format('Q'),
        [TemplateEntityVariableEnum.PREVIOUS_QUARTER]: mDate.clone().subtract(1, 'quarter').format('Q'),
        [TemplateEntityVariableEnum.NEXT_QUARTER]: mDate.clone().add(1, 'quarter').format('Q'),
        [TemplateEntityVariableEnum.CURRENT_YEAR]: mDate.format('YYYY'),
        [TemplateEntityVariableEnum.PREVIOUS_YEAR]: mDate.clone().subtract(1, 'year').format('YYYY'),
        [TemplateEntityVariableEnum.NEXT_YEAR]: mDate.clone().add(1, 'year').format('YYYY'),
        [TemplateEntityVariableEnum.TELEGRAM_PRIVATE_LINK]: telegramLink,
      },
      {
        before: TemplateEntityVariableEngine.VARIABLE_OPEN,
        after: TemplateEntityVariableEngine.VARIABLE_CLOSE,
      },
    );
  }

  private quot(value: any): string {
    return `${TemplateEntityVariableEngine.VARIABLE_OPEN}${value}${TemplateEntityVariableEngine.VARIABLE_CLOSE}`;
  }
}
